import { useState, useRef, useEffect } from 'react'
import ChangePasswordModal from './ChangePasswordModal'

interface HeaderProps {
  currentPage: string
  userInfo: {
    email: string
    fullName: string
    isAdmin: boolean
    permissions: string[]
  }
  onLogout: () => void
}

const pageTitles: { [key: string]: string } = {
  'dashboard': '대시보드',
  'raw-data': '원천 데이터 관리',
  'image-explorer': '이미지 탐색',
  'label-data': '라벨 데이터 관리',
  'dataset': '학습 데이터셋 관리',
  'training': '학습 이력 관리',
  'deployment': '모델 배포 관리',
  'users': '사용자 관리',
  'report': '수집 리포트'
}

export default function Header({ currentPage, userInfo, onLogout }: HeaderProps) {
  const [showDropdown, setShowDropdown] = useState(false)
  const [showPasswordModal, setShowPasswordModal] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <>
      <header style={{
        height: '80px',
        backgroundColor: 'white',
        borderBottom: '1px solid #e5e7eb',
        padding: '0 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
      }}>
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: '700', color: '#1f2937', margin: '0' }}>
            {pageTitles[currentPage] || '대시보드'}
          </h1>
        </div>

        <div style={{ display: 'flex', alignItems: 'center' }}>
          {/* 사용자 정보 + 배지 (클릭 → 드롭다운) */}
          <div ref={dropdownRef} style={{ position: 'relative' }}>
            <button
              onClick={() => setShowDropdown(!showDropdown)}
              style={{
                display: 'flex', alignItems: 'center', gap: '10px',
                padding: '8px 16px', backgroundColor: showDropdown ? '#f3f4f6' : 'transparent',
                border: '1px solid transparent', borderRadius: '8px',
                cursor: 'pointer', transition: 'all 0.2s',
              }}
              onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#f3f4f6' }}
              onMouseLeave={e => { if (!showDropdown) e.currentTarget.style.backgroundColor = 'transparent' }}
            >
              {/* 아바타 */}
              <div style={{
                width: '36px', height: '36px', borderRadius: '50%',
                backgroundColor: '#e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '14px', fontWeight: '600', color: '#4b5563',
              }}>
                {userInfo.fullName?.charAt(0) || 'U'}
              </div>
              <div style={{ textAlign: 'left' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937' }}>
                    {userInfo.fullName}
                  </span>
                  <span style={{
                    padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '600',
                    backgroundColor: userInfo.isAdmin ? '#dc2626' : '#059669', color: 'white',
                  }}>
                    {userInfo.isAdmin ? '관리자' : '사용자'}
                  </span>
                </div>
                <div style={{ fontSize: '12px', color: '#6b7280' }}>
                  {userInfo.email}
                </div>
              </div>
              {/* 화살표 */}
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2"
                style={{ transform: showDropdown ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
                <polyline points="6,9 12,15 18,9"></polyline>
              </svg>
            </button>

            {/* 드롭다운 메뉴 */}
            {showDropdown && (
              <div style={{
                position: 'absolute', top: '100%', right: 0, marginTop: '4px',
                backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px',
                boxShadow: '0 10px 25px rgba(0,0,0,0.1)', minWidth: '180px', zIndex: 100,
                overflow: 'hidden',
              }}>
                <button
                  onClick={() => { setShowDropdown(false); setShowPasswordModal(true) }}
                  style={{
                    width: '100%', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '10px',
                    backgroundColor: 'transparent', border: 'none', cursor: 'pointer',
                    fontSize: '14px', color: '#374151', textAlign: 'left',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#f9fafb' }}
                  onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent' }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                  </svg>
                  비밀번호 변경
                </button>
                <div style={{ height: '1px', backgroundColor: '#e5e7eb' }} />
                <button
                  onClick={() => { setShowDropdown(false); onLogout() }}
                  style={{
                    width: '100%', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '10px',
                    backgroundColor: 'transparent', border: 'none', cursor: 'pointer',
                    fontSize: '14px', color: '#dc2626', textAlign: 'left',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#fef2f2' }}
                  onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent' }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                    <polyline points="16,17 21,12 16,7"></polyline>
                    <line x1="21" y1="12" x2="9" y2="12"></line>
                  </svg>
                  로그아웃
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <ChangePasswordModal
        isOpen={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
      />
    </>
  )
}
